import React, { useEffect, useCallback } from 'react';

function ImageViewer({ image, images, currentIndex, onClose, onNavigate, nsfwPreference, onDelete }) {
  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowLeft') onNavigate(-1);
    if (e.key === 'ArrowRight') onNavigate(1);
  }, [onClose, onNavigate]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  const handleDelete = async () => {
    if (!confirm('Delete this image?')) return;
    
    try {
      const response = await fetch(`/api/images/${image.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        onClose();
        if (onDelete) onDelete();
      }
    } catch (err) {
      console.error('Failed to delete image:', err);
    }
  };

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = `/api/images/${image.id}/download`;
    link.download = image.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    const mb = bytes / (1024 * 1024);
    return mb > 1 ? `${mb.toFixed(2)} MB` : `${(bytes / 1024).toFixed(2)} KB`;
  };

  const shouldBlur = () => {
    if (!image.is_nsfw) return false;
    if (nsfwPreference === 'show') return false;
    return true;
  };

  const blurImage = shouldBlur();

  return (
    <div className="image-viewer-overlay" onClick={onClose}>
      <div className="image-viewer" onClick={(e) => e.stopPropagation()}>
        <button className="viewer-close" onClick={onClose}>✕</button>
        
        <div className="viewer-main">
          <button 
            className="nav-btn prev"
            onClick={() => onNavigate(-1)}
            disabled={currentIndex === 0}
          >
            ←
          </button>
          
          <div className={`viewer-image ${blurImage ? 'nsfw-blur' : ''}`}>
            <img 
              src={`/api/images/${image.id}/file`}
              alt={image.title || image.filename}
            />
            {blurImage && (
              <div className="nsfw-warning">
                <p>NSFW Content</p>
                <small>Change settings to view</small>
              </div>
            )}
          </div>
          
          <button 
            className="nav-btn next"
            onClick={() => onNavigate(1)}
            disabled={currentIndex === images.length - 1}
          >
            →
          </button>
        </div>

        <div className="viewer-sidebar">
          <div className="viewer-info">
            <h3>{image.title || 'Untitled'}</h3>
            
            <div className="info-grid">
              {image.artist && (
                <div className="info-item">
                  <label>Artist</label>
                  <span>{image.artist}</span>
                </div>
              )}
              
              <div className="info-item">
                <label>Dimensions</label>
                <span>{image.width}x{image.height}</span>
              </div>
              
              <div className="info-item">
                <label>Size</label>
                <span>{formatFileSize(image.size_bytes)}</span>
                {image.is_compressed && (
                  <small className="compressed-tag">
                    Compressed from {formatFileSize(image.compressed_size_bytes)}
                  </small>
                )}
              </div>
              
              {image.source_site && (
                <div className="info-item">
                  <label>Source</label>
                  <span>{image.source_site}</span>
                </div>
              )}
              
              {image.gallery_name && (
                <div className="info-item">
                  <label>Gallery</label>
                  <span>{image.gallery_name}</span>
                </div>
              )}
              
              {image.rating && (
                <div className="info-item">
                  <label>Rating</label>
                  <span className={`rating ${image.rating}`}>{image.rating}</span>
                </div>
              )}
            </div>

            {image.tags && (
              <div className="tags-section">
                <label>Tags</label>
                <div className="tags-list">
                  {image.tags.split(',').map(tag => (
                    <span key={tag.trim()} className="tag">{tag.trim()}</span>
                  ))}
                </div>
              </div>
            )}

            {image.custom_tags && (
              <div className="tags-section">
                <label>Custom Tags</label>
                <div className="tags-list">
                  {image.custom_tags.split(',').map(tag => (
                    <span key={tag.trim()} className="tag custom">{tag.trim()}</span>
                  ))}
                </div>
              </div>
            )}

            <div className="info-item">
              <label>Downloaded</label>
              <span>{new Date(image.downloaded_at).toLocaleDateString()}</span>
            </div>
          </div>

          <div className="viewer-actions">
            <button className="btn-primary" onClick={handleDownload}>
              Download
            </button>
            <button className="btn-danger" onClick={handleDelete}>
              Delete
            </button>
          </div>

          <div className="viewer-counter">
            {currentIndex + 1} / {images.length}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ImageViewer;